import type { Database } from 'sql.js';
import type { PuntosEntregaRepositoryPort } from '../../domain/ports/PuntosEntregaRepositoryPort.ts';
import type {
  CatalogRow,
  ParticipantePuntosEntregaBusquedaPrefill,
  ParticipanteResumen,
  PuntoEntregaDetail,
  PuntoEntregaUploadedCleanupSummary,
  PuntosEntregaListPage,
  PuntosEntregaListQuery,
  SavePuntoEntregaPayload,
} from '../../domain/puntoEntregaModel.ts';
import {
  listBarriosByComunaCatalog,
  listComunasCatalog,
  listEntregasCatalog,
  listLugaresByBarrioCatalog,
  listLugaresByComunaCatalog,
  findPuntosEntregaCatalogoById,
  listParentescoCatalog,
  listTiposBonosCatalog,
  listTiposDocumentoCatalog,
} from './sqlJsPuntosEntrega/catalogReads.ts';
import { getPuntoEntregaDetail } from './sqlJsPuntosEntrega/getPuntoEntregaDetail.ts';
import { listPuntosEntregaPage } from './sqlJsPuntosEntrega/listPuntosEntregaPage.ts';
import { deleteUploadedPuntoEntregaInDb } from './sqlJsPuntosEntrega/deleteUploadedPuntoEntrega.ts';
import { listUploadedOfflinePuntosEntrega as queryUploadedOfflinePuntosEntrega } from './sqlJsPuntosEntrega/listUploadedOfflinePuntosEntrega.ts';
import { countPuntosEntregaPendingOfflineExport } from './sqlJsPuntosEntrega/countPuntosEntregaPendingOfflineExport.ts';
import {
  findConflictingPuntoEntregaIdForParticipante,
  findParticipanteByDocumento,
  findParticipanteById,
  findParticipantePuntosEntregaBusquedaPrefillPorDocumento,
  findPuntoEntregaIdForParticipante,
  listParticipantePuntosEntregaBusquedaCandidates,
  participanteTipoBonoCatalogId,
  resolveParticipantePuntosEntregaBusquedaPrefill,
  resolveParticipantePuntosEntregaBusquedaPrefillByParticipanteId,
} from './sqlJsPuntosEntrega/participantReads.ts';
import { lookupBonoNombreById } from '@core/infrastructure/driven/sqljs/sqlJsLookupBonoNombre.ts';
import { ensureBonoIdForPuntoEntregaCaptura } from '@core/infrastructure/driven/sqljs/sqlJsBonoFkFromCaption.ts';
import { savePuntoEntregaTransaction } from './sqlJsPuntosEntrega/savePuntoEntrega.ts';

/** SQLite (sql.js) implementation of {@link PuntosEntregaRepositoryPort}. */
export class SqlJsPuntosEntregaRepository implements PuntosEntregaRepositoryPort {
  constructor(
    private readonly db: Database,
    private readonly filesPublicPrefix: string,
    private readonly schedulePersist: () => void,
  ) {
    this.db.run('PRAGMA foreign_keys = ON');
  }

  listPuntosEntrega(q: PuntosEntregaListQuery): PuntosEntregaListPage {
    return listPuntosEntregaPage(this.db, q);
  }

  getPuntoEntrega(id: number): PuntoEntregaDetail | null {
    return getPuntoEntregaDetail(this.db, this.filesPublicPrefix, id);
  }

  savePuntoEntrega(payload: SavePuntoEntregaPayload): number {
    return savePuntoEntregaTransaction(this.db, this.schedulePersist, payload);
  }

  listEntregas(): CatalogRow[] {
    return listEntregasCatalog(this.db);
  }

  listComunas(): CatalogRow[] {
    return listComunasCatalog(this.db);
  }

  listBarriosByComuna(comunaId: number): CatalogRow[] {
    return listBarriosByComunaCatalog(this.db, comunaId);
  }

  listLugaresByBarrio(barrioId: number): CatalogRow[] {
    return listLugaresByBarrioCatalog(this.db, barrioId);
  }

  listLugaresByComuna(comunaId: number): CatalogRow[] {
    return listLugaresByComunaCatalog(this.db, comunaId);
  }

  findPuntosEntregaCatalogoById(id: number) {
    return findPuntosEntregaCatalogoById(this.db, id);
  }

  listTiposDocumento(): CatalogRow[] {
    return listTiposDocumentoCatalog(this.db);
  }

  listTiposBonos(): CatalogRow[] {
    return listTiposBonosCatalog(this.db);
  }

  listParentesco(): CatalogRow[] {
    return listParentescoCatalog(this.db);
  }

  findParticipanteByDocumento(documento: string): ParticipanteResumen | null {
    return findParticipanteByDocumento(this.db, documento);
  }

  findParticipanteById(id: number): ParticipanteResumen | null {
    return findParticipanteById(this.db, id);
  }

  findParticipantePuntosEntregaBusquedaPrefillPorDocumento(
    documento: string,
  ): ParticipantePuntosEntregaBusquedaPrefill | null {
    return findParticipantePuntosEntregaBusquedaPrefillPorDocumento(this.db, this.filesPublicPrefix, documento);
  }

  resolveParticipantePuntosEntregaBusquedaPrefill(documento: string) {
    return resolveParticipantePuntosEntregaBusquedaPrefill(this.db, this.filesPublicPrefix, documento);
  }

  resolveParticipantePuntosEntregaBusquedaPrefillByParticipanteId(participanteId: number) {
    return resolveParticipantePuntosEntregaBusquedaPrefillByParticipanteId(
      this.db,
      this.filesPublicPrefix,
      participanteId,
    );
  }

  listParticipanteBusquedaCandidates(documentoONombre: string) {
    return listParticipantePuntosEntregaBusquedaCandidates(this.db, documentoONombre);
  }

  findPuntoEntregaIdForParticipante(participanteId: number): number | null {
    return findPuntoEntregaIdForParticipante(this.db, participanteId);
  }

  findConflictingPuntoEntregaIdForParticipante(
    participanteId: number,
    entregaCatalogId: number,
    excludedPuntoEntregaId: number | null,
  ): number | null {
    return findConflictingPuntoEntregaIdForParticipante(
      this.db,
      participanteId,
      entregaCatalogId,
      excludedPuntoEntregaId,
    );
  }

  resolveBonoIdForPuntoEntregaCaptura(caption: string, tipoBonoCatalogId: number): number | null {
    const r = ensureBonoIdForPuntoEntregaCaptura(this.db, caption, tipoBonoCatalogId);
    if (r == null) return null;
    if (r.inserted) this.schedulePersist();
    return r.bonoId;
  }

  participanteTipoBonoCatalogId(participanteId: number): number | null {
    return participanteTipoBonoCatalogId(this.db, participanteId);
  }

  getBonoNombreById(bonoId: number): string | null {
    if (!Number.isFinite(bonoId) || bonoId <= 0) return null;
    return lookupBonoNombreById(this.db, bonoId);
  }

  listUploadedOfflinePuntosEntrega(): PuntoEntregaUploadedCleanupSummary[] {
    return queryUploadedOfflinePuntosEntrega(this.db);
  }

  countPendingOfflineExport(): number {
    return countPuntosEntregaPendingOfflineExport(this.db);
  }

  deleteUploadedPuntoEntrega(id: number): void {
    deleteUploadedPuntoEntregaInDb(this.db, this.schedulePersist, id);
  }
}
